/**
 * Depends on ENV where our app works, not at build type.
 */
export const isProduction = window.__ENV__ === 'prod'

export const BACKEND_SERVER = window.__BACKEND_SERVER__

export const BACKEND_URL = `${BACKEND_SERVER}/dashboard/api/v1`
export const BACKEND_URL_V2 = `${BACKEND_SERVER}/dashboard/api/v2`
export const BACKEND_URL_V3 = `${BACKEND_SERVER}/dashboard/api/v3`

export const CAPTCHA_SITE_KEY = window.__CAPTCHA_SITE_KEY__
export const USERFLOW_SECRET = window.__USERFLOW_SECRET__

if (!USERFLOW_SECRET) {
  console.warn(
    `>> ERROR: No userflow secret specified. Please set the USERFLOW_SECRET environment variable to enable userflow.js.`,
  )
}

export const SEGMENT_WRITE_KEY = window.__SEGMENT_WRITE_KEY__
export const MIXPANEL_TOKEN = window.__MIXPANEL_TOKEN__

export const INTERCOM_APP_ID = 'itvrpzjj'
export const INTERCOM_API_BASE = 'https://api-iam.intercom.io'

export const SENTRY_DSN = window.__SENTRY_DSN__

export const creatorIdPlaceholder = ':creatorId'

export interface Route<ToUrl> {
  asRouteDescriptor: () => string
  asUrl: ToUrl
  toString: () => string
  target: '_blank' | '_self'
}

function externalPath(path: string): Route<() => string> {
  return {
    asRouteDescriptor: () => path,
    asUrl: () => path,
    toString: () => path,
    target: '_blank',
  }
}

function staticPath(path: string): Route<() => string> {
  return {
    asRouteDescriptor: () => path,
    asUrl: () => path,
    toString: () => path,
    target: '_self',
  }
}

function dynamicPath<T>(
  names: T[],
  format: (...args: T[]) => string,
): Route<(...args: T[]) => string> {
  const descriptor = format(...names)

  return {
    asRouteDescriptor: () => descriptor,
    toString: () => descriptor,
    asUrl: format,
    target: '_self',
  }
}

function creatorPath(
  suffix: string,
): Route<(creatorId: string | number) => string> {
  const descriptor = `/c/${creatorIdPlaceholder}${suffix}`

  return {
    asRouteDescriptor: () => descriptor,
    toString: () => descriptor,
    asUrl: (creatorId: string | number) => `/c/${creatorId}${suffix}`,
    target: '_self',
  }
}

function dynamicCreatorPath<T>(
  names: T[],
  format: (...args: T[]) => string,
): Route<(creatorId: string | number, ...args: T[]) => string> {
  const descriptorSuffix = format(...names)
  const descriptor = `/c/${creatorIdPlaceholder}/${descriptorSuffix}`

  return {
    asRouteDescriptor: () => descriptor,
    toString: () => descriptor,
    asUrl: (creatorId: string | number, ...args: T[]) => {
      const suffix = format(...args)

      return `/c/${creatorId}${suffix}`
    },
    target: '_self',
  }
}

export const AppRoutes = {
  Home: staticPath('/'),
  Overview: staticPath('/overview'),
  ChatterLeaderboardBrowser: dynamicPath(
    [':name'],
    (name: string) =>
      `/overview/chatter-leaderboard/browser/${
        name === ':name' ? ':name' : encodeURIComponent(name)
      }`,
  ),
  ChatterLeaderboardUser: dynamicPath(
    [':userId'],
    (userId: string) =>
      `/overview/chatter-leaderboard/user/${
        userId === ':userId' ? ':userId' : userId
      }`,
  ),
  Auth: {
    Login: staticPath('/login'),
    Register: staticPath('/register'),
    RegisterAfterPayment: staticPath('/register-after-payment'),
    ResetPassword: staticPath('/reset-password'),
    ResetPasswordWithToken: dynamicPath(
      [':token'],
      (token: string) => `/reset-password/${token}`,
    ),
    ActivateAccount: staticPath('/team/activate'),
    ApplyPromo: staticPath('/apply-promo'),
  },
  Questionnaire: staticPath('/questionnaire'),
  Tasks: {
    List: staticPath('/tasks'),
    ListDetails: dynamicPath([':id'], (id: string | number) => `/tasks/${id}`),
  },
  Courses: {
    List: staticPath('/courses'),
    Details: dynamicPath(
      [':category'],
      (category: string) => `/courses/${category}`,
    ),
  },
  Prospects: {
    List: staticPath('/prospects'),
  },
  Creators: {
    List: staticPath('/creators'),
    Access: dynamicPath(
      [':id'],
      (id: number | string) => `/creators/${id}/access`,
    ),
  },
  RestrictedWords: {
    List: staticPath('/blacklisted-words'),
    Logs: staticPath('/blacklisted-words/logs'),
  },
  Teams: {
    Base: staticPath('/team'),
    Members: {
      List: staticPath('/team/members'),
      Details: dynamicPath(
        [':id'],
        (id: string | number) => `/team/members/${id}`,
      ),
    },
    Roles: {
      List: staticPath('/team/roles'),
      Edit: dynamicPath(
        [':roleId'],
        (id: string | number) => `/team/roles/${id}`,
      ),
    },
  },
  Shifts: {
    List: staticPath('/shifts'),
    History: staticPath('/shifts/history'),
  },
  Profile: {
    Settings: staticPath('/profile/settings'),
    GeneralSettings: staticPath('/profile/preferences'),
    Security: staticPath('/profile/security'),
    AboutUs: staticPath('/profile/about-us'),
  },
  Creator: {
    ChatterTracking: {
      NewSubscriberAnalysis: creatorPath('/tracking/new-subscriber-analysis'),
      Chatter: {
        List: creatorPath('/tracking/chatter'),
        Details: dynamicCreatorPath(
          [':name'],
          (name: string) => `/tracking/chatter/${encodeURIComponent(name)}`,
        ),
      },
      Ppv: {
        List: creatorPath('/tracking/ppv'),
        Details: dynamicCreatorPath(
          [':categoryIndex'],
          (name: string) => `/tracking/ppv/${name}`,
        ),
        DetailsMediaSetMessages: dynamicCreatorPath(
          [':categoryIndex', ':mediaSetIndex'],
          (categoryIndex: string, mediaSetIndex: string) =>
            `/tracking/ppv/${categoryIndex}/messages/${mediaSetIndex}`,
        ),
      },
      Links: {
        List: creatorPath('/tracking/links'),
        Details: dynamicCreatorPath(
          [':linkId'],
          (linkId: string) => `/tracking/links/${linkId}`,
        ),
      },
      FreeTrialLinks: {
        List: creatorPath('/tracking/free-trial-links'),
        Details: dynamicCreatorPath(
          [':linkId'],
          (linkId: string) => `/tracking/free-trial-links/${linkId}`,
        ),
      },
      MessageHistory: creatorPath('/tracking/message-history'),
    },
    Statistics: {
      TikTok: creatorPath('/statistics/tiktok'),
      TikTokAccounts: creatorPath('/statistics/tiktok/accounts'),
      OnlyFans: creatorPath('/statistics/onlyfans'),
      InstagramReels: creatorPath('/statistics/ig-reels'),
      InstagramReelsAccounts: creatorPath('/statistics/ig-reels/accounts'),
    },
    AiFeatures: {
      AiSettings: creatorPath('/ai-features/settings'),
      AICHAlpha: creatorPath('/ai-features/aich'),
    },
    Settings: {
      Automations: {
        Index: creatorPath('/settings/automations'),
        AutoFollow: creatorPath('/settings/automations/auto-follow'),
        FanNumbering: creatorPath('/settings/automations/fan-numbering'),
        Promotion: creatorPath('/settings/automations/promotion'),
      },
      Scripts: {
        List: creatorPath('/settings/scripts'),
        Create: creatorPath('/settings/scripts/create'),
        Edit: dynamicCreatorPath(
          [':id'],
          (id: string) => `/settings/scripts/${id}/edit`,
        ),
      },
      WelcomeMessages: creatorPath('/settings/welcome-messages'),
      PpvFollowUpMessages: creatorPath('/settings/ppv-follow-up-messages'),
      RebillMessages: creatorPath('/settings/rebill-messages'),
      OnlineMassMessages: creatorPath('/settings/online-mass-messages'),
      ExpiringFans: creatorPath('/settings/expiring-fans'),
      Vault: {
        Index: creatorPath('/settings/vault'),
      },
      Lists: {
        Index: creatorPath('/settings/lists'),
        SpendingLists: creatorPath('/settings/lists/spending-lists'),
        NewSubsList: creatorPath('/settings/lists/new-subs-list'),
        NoSpenders: creatorPath('/settings/lists/no-spenders'),
        RecentSpenders: creatorPath('/settings/lists/recent-spenders'),
        SharedFollowers: creatorPath('/settings/lists/shared-followers'),
        CustomLists: creatorPath('/settings/lists/custom-lists'),
      },
      DisplaySettings: creatorPath('/settings/display'),
      AutomationSettings: creatorPath('/settings/automation'),
      VisibilitySettings: creatorPath('/settings/visibility'),
    },
    Funnel: {
      Index: creatorPath('/tracking/funnel'),
    },
  },
  TiktokResearch: {
    Videos: creatorPath('/tiktok/videos'),
    Sounds: {
      Index: creatorPath('/tiktok/sounds'),
      TrendingVideos: dynamicCreatorPath(
        [':soundId'],
        (id: string) => `/tiktok/sounds/${id}/trending-videos`,
      ),
    },
    Lists: {
      Index: creatorPath('/tiktok/lists'),
      Details: {
        Index: dynamicCreatorPath(
          [':id'],
          (id: string) => `/tiktok/lists/${id}`,
        ),
        TrendingVideos: dynamicCreatorPath(
          [':listId', ':soundId'],
          (listId: string, soundId: string) =>
            `/tiktok/lists/${listId}/trending-videos/${soundId}`,
        ),
      },
    },
  },
  Licenses: {
    Index: staticPath('/licenses'),
    YourLicenses: staticPath('/licenses/your-licenses'),
  },
  Help: {
    VipSupport: staticPath('/vip-support'),
    Support: externalPath('https://help.creatorhero.com'),
    DownloadApp: externalPath('https://creatorhero.com/download'),
  },
  Chatting: {
    Base: staticPath('/chatting-ui'),
    ChatWindow: dynamicPath(
      [':creatorId'],
      (creatorId: string | number) => `/chatting-ui/${creatorId}`,
    ),
  },
  Accounting: {
    Index: staticPath('/accounting'),
  },
} as const

export const TrackingRoutePart = 'tracking'

export const TrackingRouteChatterSuffix = 'chatter'
export const TrackingRoutePpvSuffix = 'ppv'
export const TrackingRouteNewSubscriberAnalysisSuffix =
  'new-subscriber-analysis'
export const TrackingRouteLinksSuffix = 'links'
export const TrackingRouteFreeTrialLinksSuffix = 'free-trial-links'
export const StatisticsRoutePart = 'statistics'
export const StatisticsRouteTiktokSuffix = 'tiktok'
export const StatisticsRouteInstagramReelsSuffix = 'ig-reels'
export const StatisticsRouteOnlyFansSuffix = 'onlyfans'
export const SettingsRoutePart = 'settings'
export const AiFeaturesRoutePart = 'ai-features'
export const AiFeaturesRoutesSettingsSuffix = 'settings'
export const AiFeaturesRoutesAICHAlphaSuffix = 'aich'
export const SettingsRouteAutomationsSuffix = 'automations'
export const SettingsRouteAutomationsAutoFollowSuffix = 'auto-follow'
export const SettingsRouteAutomationsFanNumberingSuffix = 'fan-numbering'
export const SettingsRouteAutomationsPromotionSuffix = 'promotion'
export const CreatorSettingsScriptsSuffix = 'scripts'
export const WelcomeMessagesRoutePart = 'welcome-messages'
export const PpvFollowUpMessagesRoutePart = 'ppv-follow-up-messages'
export const RebillMessagesRoutePart = 'rebill-messages'
export const OnlineMassMessagesRoutePart = 'online-mass-messages'
export const ExpiringFansRoutePart = 'expiring-fans'
export const ListsRoutePart = 'lists'
export const VaultRoutePart = 'vault'
export const ListsRouteSpendingListsSuffix = 'spending-lists'
export const ListsRouteNewSubsListSuffix = 'new-subs-list'
export const ListsRouteNoSpendersSuffix = 'no-spenders'
export const ListsRouteRecentSpendersSuffix = 'recent-spenders'
export const ListsRouteSharedFollowersSuffix = 'shared-followers'
export const ListsRouteCustomListsSuffix = 'custom-lists'
export const DisplaySettingsRoutePart = 'display'
export const AutomationSettingsRoutePart = 'automation'
export const VisibilitySettingsRoutePart = 'visibility'
export const MessageHistoryRoutePart = 'message-history'
export const FunnelMonitoringRoutePart = 'funnel'
